import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen hero-bg px-6 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 hover:text-rose-600 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </button>

        <div className="glass-card p-8">
          <div className="flex items-center justify-center mb-6">
            <FileText className="w-12 h-12 text-rose-600 mr-3" />
            <h1 className="text-3xl md:text-4xl font-bold gradient-text">Terms of Service</h1>
          </div>

          <p className="text-gray-600 text-center mb-8">
            Last Updated: January 2025
          </p>

          <div className="space-y-8 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing and using LuvCup ("the Service"), you accept and agree to be bound by these 
                Terms of Service. If you do not agree to these terms, please do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Description of Service</h2>
              <p className="mb-3">
                LuvCup provides:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Interactive relationship and love language assessments</li>
                <li>AI-powered personalized insights and recommendations</li>
                <li>Couples mode for partner compatibility analysis</li>
                <li>Downloadable PDF reports</li>
                <li>AI chat assistant for relationship guidance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">3. User Accounts</h2>
              <p className="mb-3">You agree to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your password and account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Be responsible for all activities that occur under your account</li>
                <li>Be at least 17 years old to use the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">4. User Conduct</h2>
              <p className="mb-3">You agree NOT to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Attempt to gain unauthorized access to other users' accounts</li>
                <li>Upload viruses or malicious code</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Impersonate any person or entity</li>
                <li>Scrape, copy, or misuse our content without permission</li>
                <li>Use automated systems to access the Service without authorization</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Intellectual Property</h2>
              <p>
                All content, features, and functionality of LuvCup, including but not limited to text, 
                graphics, logos, quizzes, AI-generated insights, and software, are owned by LuvCup and 
                protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p className="mt-3">
                <strong>Your Content:</strong> You retain ownership of your quiz responses and personal 
                data. By using the Service, you grant us a license to use your data to provide personalized 
                insights and services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Disclaimer of Warranties</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="font-semibold mb-2">IMPORTANT:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>LuvCup is for <strong>educational and entertainment purposes only</strong></li>
                  <li>It is NOT a substitute for professional relationship counseling or therapy</li>
                  <li>AI-generated insights are algorithmic and may not be accurate for everyone</li>
                  <li>We do not guarantee specific relationship outcomes</li>
                  <li>The Service is provided "AS IS" without warranties of any kind</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, LuvCup shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages, including but not limited to 
                relationship decisions, emotional distress, or loss of data resulting from your use of 
                the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Modifications to Service</h2>
              <p>
                We reserve the right to modify, suspend, or discontinue any part of the Service at any 
                time without notice. We may also update these Terms of Service periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Account Termination</h2>
              <p className="mb-3">
                We may terminate or suspend your account immediately, without prior notice, if you:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violate these Terms of Service</li>
                <li>Engage in fraudulent or illegal activities</li>
                <li>Abuse or misuse the Service</li>
              </ul>
              <p className="mt-3">
                You may delete your account at any time through your dashboard settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">10. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with applicable laws. 
                Any disputes shall be resolved through binding arbitration.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">11. Contact Information</h2>
              <p className="mb-3">
                For questions about these Terms of Service, contact us at:
              </p>
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-4 rounded-lg">
                <p className="font-semibold">Email: legal@luvcup.app</p>
                <p className="font-semibold">Support: support@luvcup.app</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">12. Acknowledgment</h2>
              <p>
                By using LuvCup, you acknowledge that you have read, understood, and agree to be bound 
                by these Terms of Service and our Privacy Policy.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
